import type React from "react";
import { useContext, useState } from "react";
import { ModalContext } from "src/components/Modals/context/ModalContext";
import { modals } from "src/components/Modals/context/modals";

interface ModalStackItem {
	name: string;
	props: Record<string, unknown>;
}

export const useModals = () => {
	const context = useContext(ModalContext);
	if (!context) {
		throw new Error("useModals musi być użyty wewnątrz ModalProvider.");
	}
	return context;
};

interface ModalProviderProps {
	children: React.ReactNode;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
	const [modalStack, setModalStack] = useState<ModalStackItem[]>([]);

	const openModal = (
		modalName: string,
		props: Record<string, unknown> = {},
	) => {
		if (modals[modalName]) {
			const newModal = { name: modalName, props };
			setModalStack((prevStack) => [...prevStack, newModal]);
		} else {
			console.error(`Modal o nazwie "${modalName}" nie istnieje.`);
		}
	};

	const closeModal = () => {
		setModalStack((prevStack) => prevStack.slice(0, -1));
	};

	const closeAllModals = () => {
		setModalStack([]);
	};

	const renderModal = () => {
		const latestModal = modalStack[modalStack.length - 1];
		if (!latestModal) {
			return null;
		}
		const ModalComponent = modals[latestModal.name];
		const modalProps = { ...latestModal.props, onClose: closeModal };

		return <ModalComponent {...modalProps} />;
	};

	return (
		<ModalContext.Provider value={{ openModal, closeModal, closeAllModals }}>
			{children}
			{renderModal()}
		</ModalContext.Provider>
	);
};

if (import.meta.env.DEV && import.meta.hot) {
	import.meta.hot.invalidate();
}
